import type { Point } from './orthogonal-router'

/** Same tolerance `routeIsoChain` uses to drop a duplicate (route-iso-chain.ts). */
const COINCIDENT_EPS = 1e-6

/**
 * Partition a stored interior chain at a rendered-segment hit.
 *
 * The rendered polyline is `[srcTerminal, ...chain, tgtTerminal]` — `edgePolyline` does
 * no routing and injects no doglegs (measured) — so rendered segment `k` spans stored
 * points `chain[k-1] → chain[k]`, and the partition is simply `chain[0..k-1]` /
 * `chain[k..]`.
 *
 * ⚠️ An EMPTY half is a correct result, not a bug: it means that half is a single
 * straight run with no interior bends. `k = 0` (source seam) and `k = chain.length`
 * (target seam) produce exactly that.
 *
 * ⚠️ When `splitPoint` coincides with a stored point, that point BECOMES the split
 * junction and must be excluded from BOTH halves. Leaving it in hands the downstream
 * half a duplicate of its own `src`, which `routeIsoChain` then silently drops via
 * `COINCIDENT_EPS` — the half comes back one bend short, with no error anywhere.
 */
export function splitChainAt(
  chain: Point[],
  segmentIndex: number,
  splitPoint: Point,
): { before: Point[]; after: Point[] } {
  const k = Math.max(0, Math.min(chain.length, segmentIndex))
  const copy = (p: Point): Point => ({ x: p.x, y: p.y })

  const coincident = chain.findIndex(
    p => Math.hypot(p.x - splitPoint.x, p.y - splitPoint.y) <= COINCIDENT_EPS,
  )
  if (coincident >= 0) {
    return {
      before: chain.slice(0, coincident).map(copy),
      after: chain.slice(coincident + 1).map(copy),
    }
  }
  return { before: chain.slice(0, k).map(copy), after: chain.slice(k).map(copy) }
}
